import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const STAR_VALUES = [1, 2, 3, 4, 5, 6, 7];

function StarPicker({ value, onSelect, disabled }) {
  return (
    <div className="flex items-center gap-1">
      {STAR_VALUES.map((n) => (
        <button
          key={n}
          type="button"
          disabled={disabled}
          onClick={() => onSelect(value === n ? null : n)}
          className="text-2xl leading-none disabled:opacity-50"
          style={{ color: n <= (value || 0) ? '#c9a961' : '#4a4a4a' }}
          aria-label={`Set ranking to ${n} star${n > 1 ? 's' : ''}`}
        >
          {n <= (value || 0) ? '★' : '☆'}
        </button>
      ))}
    </div>
  );
}

// NOTE: automatic uprank/promotion logic (based on sales volume, downline
// composition, etc.) is intentionally out of scope here -- ranks are 100%
// manually assigned by superadmin for now. Future enhancement.
export default function Ranking() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'superadmin') {
      navigate('/dashboard');
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user || user.role !== 'superadmin') return;

    const fetchMembers = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/ranking`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.error || 'Failed to load members');
        }
        setMembers(data.users || []);
      } catch (err) {
        setError(err.message || 'Failed to load members');
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, [user, token]);

  const handleSetRanking = async (memberId, ranking) => {
    setSavingId(memberId);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/set-ranking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ user_id: memberId, ranking }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to update ranking');
      }
      setMembers((prev) =>
        prev.map((m) => (m.id === memberId ? { ...m, ranking } : m))
      );
    } catch (err) {
      setError(err.message || 'Failed to update ranking');
    } finally {
      setSavingId(null);
    }
  };

  if (!user || user.role !== 'superadmin') {
    return null;
  }

  const q = searchQuery.trim().toLowerCase();
  const filteredMembers = members.filter((m) => {
    if (!q) return true;
    return (
      (m.first_name || '').toLowerCase().includes(q) ||
      (m.last_name || '').toLowerCase().includes(q) ||
      (m.email || '').toLowerCase().includes(q) ||
      (m.affiliate_code || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen px-4 pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
    <div className="max-w-4xl mx-auto">
      <h1 className="text-headline-lg font-bold mb-2" style={{ color: '#c9a961' }}>
        Member Ranking
      </h1>
      <p className="text-body-xs mb-6" style={{ color: '#999999' }}>
        Ranks are assigned manually by superadmin only. Automatic rank promotion (uprank) based on sales/network performance is not implemented — planned as a future enhancement.
      </p>

      <div className="mb-6">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name, email, or Wellis ID"
          className="w-full px-4 py-2 rounded-lg border text-body-sm focus:outline-none"
          style={{ backgroundColor: '#0d2818', borderColor: '#c9a961', color: '#c9a961' }}
        />
      </div>

      {error && (
        <div className="border rounded-lg p-4 mb-6" style={{ backgroundColor: '#2a0d0d', borderColor: '#c94961', color: '#c94961' }}>
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ color: '#b8a68f' }}>Loading members...</p>
      ) : filteredMembers.length === 0 ? (
        <div className="border rounded-lg p-6 text-center" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          <p style={{ color: '#b8a68f' }}>No members match your search</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredMembers.map((member) => (
            <div
              key={member.id}
              className="border rounded-lg p-4 flex items-center justify-between gap-4"
              style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}
            >
              <div>
                <p className="font-medium" style={{ color: '#c9a961' }}>
                  {member.first_name} {member.last_name}
                </p>
                <p className="text-body-xs" style={{ color: '#999999' }}>
                  {member.affiliate_code || '—'} · {member.email}
                </p>
              </div>
              <StarPicker
                value={member.ranking}
                disabled={savingId === member.id}
                onSelect={(ranking) => handleSetRanking(member.id, ranking)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
    </div>
  );
}
